import { useMutation, useQuery, useQueries } from '@tanstack/react-query'
import { useState, useCallback, useMemo, useEffect } from 'react'
import { 
    generateAIResponse,
    getAllMessages,
    // getAllKitchens, getKitchenID, 
    getAllResources,
    getResourceByID,
    sendMessageToUser,
    // getCommunityBookings,
    // addKitchen, updateKitchen, updateMembershipPlans, 
    // getMembershipPlan, 
    // deleteMembershipRequirement, addMembershipRequirement, 
    // addMembershipPlans, 
    getMembershipPlans,
    getAllCommunities, 
    getCommunityByID,
    // getMembershipRequirements, 
} from '../api/crud'

import { ReactComponent as DocumentationIcon } from '../icons/documentationIcon.svg'
// import { ReactComponent as PreferenceIcon } from '../icons/PreferenceIcon.svg'
import { ReactComponent as FundingIcon } from '../icons/fundingIcon.svg'
import { ReactComponent as KitchenIcon } from '../icons/KitchenIcon.svg'
// import { ReactComponent as NotificationIcon } from '../icons/NotificationIcon.svg'
// import { ReactComponent as AccountIcon } from '../icons/Account.svg'
import { ReactComponent as ExpertIcon } from '../icons/NavCommunitiesIcon.svg'
import { ReactComponent as TrainingIcon } from '../icons/trainingIcon.svg'
import { ReactComponent as FreshFoodIcon } from '../icons/FreshFoodIcon.svg'

import { ReactComponent as CommunityIcon } from '../icons/Community.svg'

import { 
    ResourceSchema, ResourceType, SavedResourceSchema, SettingsOptionsType, 
    SortByType, CommunitySchema, BookingFilterState,
    BookingSortDirection, BookingSortField,
    BookingViewMode, ResourceBookingSchema, defaultBookingFilterState,
} from '../data/types'
import { filterBookings, sortBookings, getStaffAutoReplyForMessages } from '../utils/helpers';
import { useResource } from '../contexts/appcontext'
// import { useAuthState } from '../contexts/authcontext'
// import { useMessageState } from '../contexts/messagecontext'
export { STAFF_AUTO_REPLY_MESSAGE } from '../utils/helpers';


const staleTime = 1000
const isEnabled = true

export const resourcesPerPage: number = 9

export const CHATBOT_TYPING_TIMEOUT_MS = 45_000;
export const STAFF_TYPING_DURATION_MS = 5_000;

export const useChatbotTyping = (
  currentChatCommunity: string,
  isLoadingMessage: boolean
) => {
  const [timedOut, setTimedOut] = useState(false);
  const isChatbot = currentChatCommunity === "chatbot";

  useEffect(() => {
    setTimedOut(false);
  }, [currentChatCommunity]);

  useEffect(() => {
    if (!isChatbot || !isLoadingMessage) {
      setTimedOut(false);
      return;
    }

    const timer = setTimeout(() => setTimedOut(true), CHATBOT_TYPING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isChatbot, isLoadingMessage]);

  return isChatbot && isLoadingMessage && !timedOut;
};

export const useStaffChatAcknowledgment = (
  currentChatCommunity: string,
  submitToken: number,
  messages?: { type?: 'sent' | 'received'; content?: string; created?: string }[],
  timezone?: string,
  referenceDate: Date = new Date()
) => {
  const [isStaffTyping, setIsStaffTyping] = useState(false);
  const isStaff = currentChatCommunity === 'staff';

  useEffect(() => {
    setIsStaffTyping(false);
  }, [currentChatCommunity]);

  useEffect(() => {
    if (!isStaff || submitToken === 0) return;

    setIsStaffTyping(true);

    const timer = setTimeout(() => setIsStaffTyping(false), STAFF_TYPING_DURATION_MS);
    return () => clearTimeout(timer);
  }, [submitToken, isStaff]);

  const pendingStaffAutoReply = useMemo(
    () =>
      isStaff
        ? getStaffAutoReplyForMessages(messages ?? [], undefined, timezone, referenceDate)
        : null,
    [isStaff, messages, timezone, referenceDate]
  );

  const staffAutoReply =
    isStaff && isStaffTyping ? null : pendingStaffAutoReply;

  return { isStaffTyping: isStaff && isStaffTyping, staffAutoReply };
};


export const useBookingsFilterSort = (
  bookings: ResourceBookingSchema[],
  searchTerm?: string
) => {
  const [viewMode, setViewMode] = useState<BookingViewMode>('grid');
  const [sortField, setSortField] = useState<BookingSortField>('start_time');
  const [sortDirection, setSortDirection] = useState<BookingSortDirection>('desc');
  const [filters, setFilters] = useState<BookingFilterState>(defaultBookingFilterState);
  const [showFilters, setShowFilters] = useState(false);

  const processedBookings = useMemo(() => {
    const filtered = filterBookings(bookings, filters, searchTerm);
    return sortBookings(filtered, sortField, sortDirection);
  }, [bookings, filters, searchTerm, sortField, sortDirection]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.statuses.length > 0) count++;
    if (filters.resourceTypes.length > 0) count++;
    if (filters.resourceName.trim()) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.planRateMin || filters.planRateMax) count++;
    if (filters.totalCostMin || filters.totalCostMax) count++;
    if (filters.durationMin || filters.durationMax) count++;
    return count;
  }, [filters]);

  const resetFilters = () => setFilters(defaultBookingFilterState);

  return {
    viewMode,
    setViewMode,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    processedBookings,
    activeFilterCount,
    resetFilters,
  };
};

export const useLoadResourceOptions = () => {
    const {data: allOptions, refetch: reloadOptions,  isLoading: isLoadingOptions} = useQuery({ 
        queryKey: ['options'], 
        queryFn: async () => {
            console.log("fetching resource options")
            const resourcesOptions: SettingsOptionsType[] = [
                {name: "Local Kitchen Spaces", description: "Discover kitchen communities and food incubators in your area.", icon: KitchenIcon, path: "kitchen", tags: []},
                {name: "Required Documentation & Compliance ", description: "Access essential legal documents and compliance guides", icon: DocumentationIcon, path: "documentation", tags: ["servsafe"]},
                {name: "Culinary Training", description: "Enhance your skills with top culinary training programs.", icon: TrainingIcon, path: "training", tags: ["servsafe"]},
                {name: "Funding & Financial Resources", description: "Discover financial options and resources for your business.", icon: FundingIcon, path: "funding", tags: []},
                {name: "Industry Mentors & Experts", description: "Discover organizations that offer mentorship and industry insights.", icon: ExpertIcon, path: "expert", tags: []},
                {name: "Local Fresh Food", description: "Source fresh, local ingredients for your culinary creations.", icon: FreshFoodIcon, path: "farm", tags: []}
              ]
            return resourcesOptions 
        },
        staleTime,
        enabled: isEnabled
    })

    const searchOptions = (searchTerm: string) => {
        const searchedOptions =  allOptions ? allOptions.filter((option: SettingsOptionsType) => (option.name?.toLowerCase().includes(searchTerm.toLowerCase()) || option.tags?.join(" ")?.toLowerCase()?.includes(searchTerm.toLowerCase()))) : []
        // const searchedOptions = new Set(searchedResources.map((resource: ResourceSchema) => resource.resource_type));
        return {searchedOptions, isLoadingOptions, reloadOptions}
    }

    const getResourceIcon = (resourceType: ResourceType | undefined) => {
        switch(resourceType){
            case 'kitchen':
                return KitchenIcon
            case 'documentation':
                return DocumentationIcon
            case 'training':
                return TrainingIcon
            case 'funding':
                return FundingIcon
            case 'expert':
                return ExpertIcon
            case 'farm':
                return FreshFoodIcon
            default:
                return CommunityIcon
        }
    }
    return {allOptions: allOptions ? allOptions : [], isLoadingOptions, reloadOptions, searchOptions, getResourceIcon}
}

export const useLoadMessages = (uid: string, communityId: string) => {
    const {data: messages, refetch: reloadMessages, isLoading: isLoadingMessages, isFetching: isFetchingMessages} = useQuery({ 
        queryKey: ['messages', uid, communityId], 
        queryFn: async () => {
            console.log("fetching messages")
            const res = await getAllMessages(uid, communityId)
            return res
        },
        staleTime,
        enabled: isEnabled && !!uid && !!communityId
    })

    const switchMember = async (member: string) => {
        if (member !== communityId){
            reloadMessages()
        }
    }
    return {isLoadingMessages, isFetchingMessages, messages, switchMember, reloadMessages}
}



export const useSendMessage = (uid: string, communityId: string) => {
    var { reloadMessages } = useLoadMessages(uid, communityId)
    const [pendingMessage, setPendingMessage] = useState<string | null>(null)

    const mutation = useMutation({ 
        mutationFn: async (content: string) => {
            console.log("send message") 
            if (communityId === "chatbot"){
                const res = await generateAIResponse(uid, content) 
                await reloadMessages()
                return res
            } else {
                const res = await sendMessageToUser(uid, communityId, content) 
                await reloadMessages()
                return res
            }
        },
        onMutate: (content) => {
            setPendingMessage(content)
        },
        onSettled: () => {
            setPendingMessage(null)
        },
    })

    const sendMessage = (content: string) => {
        mutation.mutate(content)
        return mutation.isSuccess
    }

    // const getAIMessage = (username: string, content: string) => {
    //     mutation.mutate({username, content})
    //     return true
    // }
    const isLoadingMessage = mutation.isPending
    return { sendMessage, isLoadingMessage, pendingMessage }
}


export const useLoadCommunities = () => {
    const {data: allCommunities, refetch: reloadCommunities,  isLoading: isLoadingCommunities} = useQuery({ 
        queryKey: ['communities'], 
        queryFn: async () => {
            console.log("fetching communities")
            var res = await getAllCommunities()
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return {allCommunities, isLoadingCommunities, reloadCommunities}
}

export const useLoadResources = (resourceType?: ResourceType, communityId?: string) => {
    const { updateResources } = useResource()
    const {data: allResources, refetch: reloadResources,  isLoading: isLoadingResources} = useQuery({ 
        queryKey: ['resources', resourceType, communityId], 
        queryFn: async () => {
            console.log("fetching resources")
            var res = await getAllResources(resourceType, communityId)
            if (res && updateResources!== undefined){
                updateResources(res)
                return res
            } 
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return {allResources, isLoadingResources, reloadResources}
}

export const useUpdateResources = (searchTerm: string)=>{
    const { updateResources } = useResource()
    const {searchOptions} = useLoadResourceOptions()

    if (searchTerm && updateResources!== undefined){
        const { searchedOptions } = searchOptions(searchTerm);
        updateResources(searchedOptions)
    } 
}


export const useLoadResourcesFilter = (resourceType: ResourceType, filterState: string, isActive: boolean, sortBy: SortByType = 'zipcode') => {
    // Use more specific query key to prevent cache issues
    const {data: allResources, refetch: reloadResources, isLoading: isLoadingResources} = useQuery({ 
        queryKey: ['resources', resourceType, filterState, isActive, sortBy], // Include all filter params in queryKey
        queryFn: async () => {
            console.log(`Fetching resources for type: ${resourceType}, state: ${filterState}, active: ${isActive}`)
            const res = await getAllResources(resourceType)
            console.log(`Retrieved ${res?.length || 0} resources from API`)
            if (res) {
                res.sort((a, b) => {
                    const aValue = a[sortBy as keyof ResourceSchema];
                    const bValue = b[sortBy as keyof ResourceSchema];

                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        return aValue.localeCompare(bValue);
                    }
                    if (typeof aValue === 'number' && typeof bValue === 'number') {
                        return aValue - bValue;
                    }
                    return 0;
                });
            }
            // console.log(`Sorted resources:`, res)
            return res ? res : []
        },
        staleTime: 0, // Don't cache
        enabled: isEnabled
    })

    // Apply filters
    const activeCheck = (resource: ResourceSchema) => resource?.active === isActive || !isActive
    const usStateCheck = (resource: ResourceSchema) => resource?.state?.toLowerCase() === filterState.toLowerCase() || resource?.state === "" || filterState === ""
    const filteredResources = allResources ? allResources.filter(resource => usStateCheck(resource) && activeCheck(resource)) : []
    
    console.log(`After filtering: ${filteredResources.length} resources remain`)

    const getPage = (resourcesList: ResourceSchema[], pageNumber: number) => {
        // Get total number of resources
        const numAllResources = resourcesList?.length || 0;
        
        // Calculate total pages
        const numPages = Math.ceil(numAllResources / resourcesPerPage);
        
        // Ensure page number is within valid range (default to page 1)
        const validPageNumber = Math.max(1, Math.min(pageNumber || 1, Math.max(1, numPages)));
        
        // Calculate start and end indices for slicing
        const startIndex = (validPageNumber - 1) * resourcesPerPage;
        const endIndex = Math.min(startIndex + resourcesPerPage, numAllResources);
        
        // Get resources for the current page - create a new array to avoid reference issues
        const paginatedResources = resourcesList.slice(startIndex, endIndex);
        
        // Count resources on the current page
        const numResources = paginatedResources.length;
        
        console.log(`Page ${validPageNumber}/${numPages}: Showing resources ${startIndex+1}-${endIndex} of ${numAllResources}`);
        
        // Log the first couple of resources to help debug
        if (paginatedResources.length > 0) {
            console.log(`First resource on page: ${paginatedResources[0].name || 'unnamed'}`);
        }
        
        return {
            paginatedResources: [...paginatedResources], // Create a new array to ensure no reference issues
            numAllResources, 
            numResources, 
            startIndex, 
            endIndex,
            currentPage: validPageNumber,
            totalPages: numPages
        };
    }

    const getSavedPage = (resourcesList: SavedResourceSchema[], pageNumber: number) => {
        // Use a specific resourcesPerPage value for saved resources
        const savedResourcesPerPage = 4;
        
        // Get total number of resources
        const numAllResources = resourcesList?.length || 0;
        
        // Calculate total pages
        const numPages = Math.ceil(numAllResources / savedResourcesPerPage);
        
        // Ensure page number is within valid range (default to page 1)
        const validPageNumber = Math.max(1, Math.min(pageNumber || 1, Math.max(1, numPages)));
        
        // Calculate start and end indices for slicing
        const startIndex = (validPageNumber - 1) * savedResourcesPerPage;
        const endIndex = Math.min(startIndex + savedResourcesPerPage, numAllResources);
        
        // Get resources for the current page - create a new array to avoid reference issues
        const paginatedResources = resourcesList.slice(startIndex, endIndex);
        
        // Count resources on the current page
        const numResources = paginatedResources.length;
        
        console.log(`Saved Page ${validPageNumber}/${numPages}: Showing saved resources ${startIndex+1}-${endIndex} of ${numAllResources}`);
        
        return {
            resourcesPerPage: savedResourcesPerPage,
            paginatedResources: [...paginatedResources], // Create a new array
            numAllResources, 
            numResources, 
            startIndex, 
            endIndex,
            currentPage: validPageNumber,
            totalPages: numPages
        };
    }

    const searchResources = (searchTerm: string) => {
        const searchAttributes: (keyof ResourceSchema)[] = ["name", "description", "location", "zipcode"]
        const searchedResources = filteredResources.filter(resource =>
          searchAttributes.some(attr => {
            const value = resource[attr];
            return (value && typeof value === 'string') && 
                   value.toLowerCase().includes(searchTerm.toLowerCase());
          })
        );
        return {searchedResources, isLoadingResources, reloadResources}
    }

    const searchSavedResources = (searchTerm: string, filteredSavedResources: SavedResourceSchema[]) => {
        const searchAttributes: (keyof SavedResourceSchema)[] = ["name", "description", "location", "zipcode"]
        const searchedResources = filteredSavedResources.filter(item =>
          searchAttributes.some(attr => {
            const value = item[attr];
            return (value && typeof value === 'string') && 
                   value.toLowerCase().includes(searchTerm.toLowerCase());
          })
        );
        return {searchedResources, isLoadingResources, reloadResources}
    }
    
    return {filteredResources, isLoadingResources, getPage, getSavedPage, searchResources, searchSavedResources, reloadResources}
}


export const useLoadCurrentResource = (initialResourceId: string) => {
    const [resourceId, setResourceId] = useState(initialResourceId);
    // let { updateCurrentResource } = useResource();

    const {data: currentResource, refetch: reloadCurrentResource, isLoading: isLoadingCurrentResource} = useQuery({ 
        queryKey: ['resource', resourceId], 
        queryFn: async () => {
            console.log("fetching resource by id");
            var res = await getResourceByID(resourceId ? resourceId : "");
            // if (res && updateCurrentResource !== undefined) {
            //     updateCurrentResource(res);
            // }
            return res;
        },
        staleTime,
        enabled: isEnabled && !!resourceId,
    });

    const updateResourceId = useCallback(async (newId: string) => {
        setResourceId(newId);
        await reloadCurrentResource();
    }, [reloadCurrentResource]);
    
    return { 
        isLoadingCurrentResource, 
        currentResource, 
        reloadCurrentResource, 
        updateResourceId 
    };
};

export const useLoadMembershipPlans = (communityId: string) => {
    const {data: allPlans, refetch: reloadPlans,  isLoading: isLoadingPlans} = useQuery({ 
        queryKey: ['membership_plans', communityId], 
        queryFn: async () => {
            console.log("fetching membership plans")
            var res = await getMembershipPlans(communityId)
            return res
        },
        staleTime,
        enabled: isEnabled
    })
    return {allPlans, isLoadingPlans, reloadPlans}
}

export const useLoadSavedResources = (savedResources: SavedResourceSchema[]) => {
    // Extract unique resource IDs from saved resources
    const resourceIds = useMemo(() => {
        const ids = savedResources
            ?.map(resource => resource.resource_id || resource.id)
            .filter((id): id is string => !!id) || [];
        // Remove duplicates
        return Array.from(new Set(ids));
    }, [savedResources]);

    // Extract unique community IDs from saved resources
    const communityIds = useMemo(() => {
        const ids = savedResources
            ?.map(resource => resource.community_id)
            .filter((id): id is string => !!id) || [];
        // Remove duplicates
        return Array.from(new Set(ids));
    }, [savedResources]);

    // Fetch full resource details for each saved resource in parallel
    const resourceQueries = useQueries({
        queries: resourceIds.map((resourceId) => ({
            queryKey: ['savedResource', resourceId],
            queryFn: async () => {
                console.log(`Fetching full resource details for saved resource: ${resourceId}`);
                const fullResource = await getResourceByID(resourceId);
                return { resourceId, fullResource };
            },
            staleTime: 0, // Don't cache - always fetch fresh data
            enabled: isEnabled && !!resourceId,
        })),
    });

    // Fetch community details for each unique community_id in parallel
    const communityQueries = useQueries({
        queries: communityIds.map((communityId) => ({
            queryKey: ['savedResourceCommunity', communityId],
            queryFn: async () => {
                console.log(`Fetching community details for saved resource community: ${communityId}`);
                const community = await getCommunityByID(communityId);
                return { communityId, community };
            },
            staleTime: 0, // Don't cache - always fetch fresh data
            enabled: isEnabled && !!communityId,
        })),
    });

    // Check if any query is loading
    const isLoading = resourceQueries.some(query => query.isLoading) || communityQueries.some(query => query.isLoading);

    // Map fetched resources back to saved resources with full data
    const enrichedSavedResources = useMemo(() => {
        if (!savedResources || savedResources.length === 0) {
            return [];
        }

        // Create a map of resourceId -> fullResource for quick lookup
        const resourceMap = new Map<string, ResourceSchema>();
        resourceQueries.forEach((query) => {
            if (query.data?.fullResource && query.data.resourceId) {
                resourceMap.set(query.data.resourceId, query.data.fullResource);
            }
        });

        // Create a map of communityId -> community for quick lookup
        const communityMap = new Map<string, CommunitySchema>();
        communityQueries.forEach((query) => {
            if (query.data?.community && query.data.communityId) {
                communityMap.set(query.data.communityId, query.data.community);
            }
        });

        // Enrich saved resources with full data, falling back to original if fetch failed
        return savedResources.map((savedResource) => {
            const resourceId = savedResource.resource_id || savedResource.id;
            const fullResource = resourceId ? resourceMap.get(resourceId) : null;
            const communityId = savedResource.community_id;
            const community = communityId ? communityMap.get(communityId) : null;

            if (fullResource) {
                // Merge full resource data with saved resource, preserving resource_id
                // Ensure both id and resource_id are set for compatibility
                // Also attach community data if available
                return {
                    ...fullResource,
                    resource_id: resourceId, // Preserve the resource_id field from saved resource
                    id: fullResource.id || resourceId, // Use full resource's id if available, fallback to resourceId
                    community: community || undefined, // Attach community data if fetched
                } as SavedResourceSchema & { community?: CommunitySchema };
            }

            // If fetch failed or still loading, return original saved resource with community if available
            return {
                ...savedResource,
                community: community || undefined,
            } as SavedResourceSchema & { community?: CommunitySchema };
        });
    }, [savedResources, resourceQueries, communityQueries]);

    return {
        enrichedSavedResources,
        isLoading,
    };
}

// export const useFeedPosts = () => {
//     const { data: feedPosts, isLoading, isError, refetch:reloadFeedPosts } = useQuery({
//         queryKey: ['feedPosts'], 
//         queryFn: async () => {
//             console.log("Getting feed posts");
//             const response = await getFeedPosts(); 
//             return response;
//         },
//         staleTime,
//         enabled: isEnabled
//     });
//     return { feedPosts, isLoading, isError, reloadFeedPosts };
// }


// export const usePaginatedResources = (resourcesList: ResourceSchema[], pageNumber: number = 1) => {
//     // // const resourcesPerPage: number = 4
//     // const fetchPaginatedData = async (page: number) => {
//     //     // return resourcesList.slice(0,8)
//     //     // return resourcesList.slice(8,16)
//     //     // return resourcesList.slice(16,24)
//     //     if (resourcesList && page && page > 0 && page <= (resourcesList.length / resourcesPerPage)){
//     //         return resourcesList.slice((page-1) * resourcesPerPage, page * resourcesPerPage)
//     //     } 
//     //     return resourcesList
//     // };

//     // const {data: pageResources, refetch: reloadPageResources,  isPending: isLoadingResources} = useQuery({
//     //     queryKey: ['resources', pageNumber],
//     //     queryFn: () => fetchPaginatedData(pageNumber),
//     //     placeholderData: keepPreviousData,
//     // })


//     // const getPage = ()

//     const {data: pageResources, refetch: reloadPageResources} = useQuery({
//         queryKey: ['resources', pageNumber],
//         queryFn: () => {
//             let numResources = resourcesList?.length ? resourcesList.length : 0
//             let numPages = Math.ceil(numResources / resourcesPerPage)
//             if (resourcesList && pageNumber && pageNumber > 0 && pageNumber <= numPages){
//                 let startIndex = (pageNumber-1) * resourcesPerPage
//                 let endIndex = (numPages == pageNumber) ? (numResources - 1) : (pageNumber * resourcesPerPage)
//                 resourcesList =  resourcesList.slice(startIndex, endIndex)
//             } 
//             return resourcesList
//         },
//         placeholderData: keepPreviousData,
//     })

//     return {pageResources, reloadPageResources}
// }

